import { SkitterBookUploadResult } from "../Models/SkitterBookUploadResult";
import { SkitterBookCriteria } from '../Models/SkitterBookCriteria'
import { SkitterFramePagedPreviewResult } from '../Models/SkitterFramePagedPreviewResult';
import { auth } from "../Services/firebase";

const getAuthUid = async (): Promise<string | null> => {
    const user = auth.currentUser;
    return user ? await user.uid : null;
};

export type SerialisationMode = "ignore" | "reject";

export async function streamSkitterbookLoad(
    file: File,
    pageCount: number,
    frameInterval: number,
    onProgress: (result: SkitterBookUploadResult) => void
): Promise<void> {
    const formData = new FormData();
    formData.append('file', file); // "file" must match your controller parameter
    formData.append('pageCount', pageCount.toString());
    formData.append('frameInterval', frameInterval.toString());

    const response = await authFetch('/api/skitterbook/upload', {
        method: 'POST',
        body: formData,
    });

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    if (!reader) throw new Error('No stream available');

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n');
        while (boundary !== -1) {
            const chunk = buffer.slice(0, boundary).trim();
            buffer = buffer.slice(boundary + 1);

            if (chunk) {
                try {
                    const parsed: SkitterBookUploadResult = JSON.parse(chunk);
                    onProgress(parsed);
                } catch (err: unknown) {
                    if (err instanceof SyntaxError) {
                        console.warn('JSON parse error:', err.message, 'Chunk:', chunk);
                    } else {
                        console.error('Unexpected error during parsing:', err);
                    }
                }
            }

            boundary = buffer.indexOf('\n');
        }
    }
}

function getCsrfToken(): string | null {
    const tokenElement = document.querySelector('meta[name="csrf-token"]');
    return tokenElement?.getAttribute('content') ?? null;
}

export const authFetch = async (url: string, options: RequestInit = {}) => {
    const authToken = await getAuthUid();
    const csrfToken = getCsrfToken();

    // If the URL is relative, prefix it with the current origin
    const qualifiedUrl = url.startsWith("http")
        ? url
        : `${window.location.origin}${url}`;

    const headers: Record<string, string> = {
        ...(options.headers as Record<string, string>),
        "X-CSRF-TOKEN": csrfToken ?? ""
    };

    if (options.body && !(options.body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
    }

    if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
    }

    console.log("Final headers:", headers);

    return fetch(qualifiedUrl, { ...options, headers, credentials: "include" });
};

export async function getSkitterFramePreviewPage(
    skitterBookToken: string,
    pageNumber: number = 0,
    pageSize: number = 20
): Promise<SkitterFramePagedPreviewResult> {

    const encodedSkitterBookToken = encodeURIComponent(skitterBookToken);

    const url = `/api/skitterbookframes/pagedlist?skitterbookToken=${encodedSkitterBookToken}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

    const response = await authFetch(url, { method: "POST" });

    if (!response.ok) {
        throw new Error(`Failed to fetch preview list: ${response.statusText}`);
    }

    const result: SkitterFramePagedPreviewResult = await response.json();
    return result;
}

export async function postUpdatedSkitterBookCriteria(changedSkitterBookCriteria: SkitterBookCriteria, SkitterBookToken: string, onChanged?: (criteria: SkitterBookCriteria) => void) {
    if (changedSkitterBookCriteria && changedSkitterBookCriteria !== null) {

        try {
            const response = await authFetch(
                `/api/skitterbook/update?skitterbookToken=${encodeURIComponent(
                    SkitterBookToken
                )}`, {
                method: "POST",
                body: JSON.stringify(changedSkitterBookCriteria)
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const updated = (await response.json()) as SkitterBookCriteria;
            if (onChanged) {
                onChanged(updated);
            }
        } catch (err) {
            console.error("Failed to update criteria", err);
        }
    }
}

export function stampUpdated<T extends { lastUpdated: string }>(
    current: T,
    partial: Partial<T>
): T {
    return {
        ...current,
        ...partial,
        lastUpdated: new Date().toISOString()
    };
}

export function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wraps an async function so that only one invocation may be in flight at a time.
 * Subsequent calls are either ignored (returning the same promise) or rejected.
 */
export function withSerialisedAsync<TArgs extends any[], TResult>(
    asyncFn: (...args: TArgs) => Promise<TResult>,
    mode: SerialisationMode = "ignore"
) {
    let currentSerialisedCall: Promise<TResult> | null = null;

    return (...args: TArgs): Promise<TResult> => {
        if (currentSerialisedCall) {
            if (mode === "ignore") {
                return currentSerialisedCall;
            }
            if (mode === "reject") {
                return Promise.reject(
                    new Error("A serialised async call is already in progress")
                );
            }
        }

        currentSerialisedCall = asyncFn(...args)
            .finally(() => {
                currentSerialisedCall = null;
            });

        return currentSerialisedCall;
    };
}

/**
 * A keyed variant of withSerialisedAsync.
 * Each distinct key maintains its own serialised in‑flight state.
 */
export function withKeyedSerialisedAsync<TKey, TArgs extends any[], TResult>(
    getKey: (...args: TArgs) => TKey,
    asyncFn: (...args: TArgs) => Promise<TResult>,
    mode: SerialisationMode = "ignore"
) {
    const serialisedCallsByKey = new Map<TKey, Promise<TResult>>();

    return (...args: TArgs): Promise<TResult> => {
        const key = getKey(...args);
        const existingCall = serialisedCallsByKey.get(key);

        if (existingCall) {
            if (mode === "ignore") {
                return existingCall;
            }
            if (mode === "reject") {
                return Promise.reject(
                    new Error(`A serialised async call is already in progress for key: ${String(key)}`)
                );
            }
        }

        const newCall = asyncFn(...args)
            .finally(() => {
                serialisedCallsByKey.delete(key);
            });

        serialisedCallsByKey.set(key, newCall);
        return newCall;
    };
}

